import { ChevronLeft, ChevronRight, ChevronsLeftRight, ChevronsRightLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { dur, enterPage, gsap } from "@/themes/rapport/motion";
import { toast } from "sonner";

import { api } from "@/api";
import { LeadCard } from "@/components/pipeline/LeadCard";
import { useCardDrag } from "@/components/pipeline/useCardDrag";
import { cleanFilters, errText, euros } from "@/components/pipeline/utils";

export const TONES = ["butter", "pine", "tomato", "ink"];
const STAGE = { butter: "qualify", pine: "proposal", tomato: "negotiate", ink: "closing" };

// Colonnes repliées en fine bande (mémorisé sur ce poste)
const useFolded = () => {
  const [folded, setFolded] = useState(() => { try { return JSON.parse(localStorage.getItem("pl-folded") || "[]"); } catch { return []; } });
  const toggle = (id) => setFolded((f) => { const n = f.includes(id) ? f.filter((x) => x !== id) : [...f, id]; localStorage.setItem("pl-folded", JSON.stringify(n)); return n; });
  return [folded, toggle];
};

const Column = ({ col, tone, filters, dormantDays, onOpen, extra, setExtra, folded, onFold }) => {
  const [loading, setLoading] = useState(false);
  const cards = [...col.cards, ...(extra || [])];
  const more = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/pipeline/search", { ...cleanFilters(filters), step_id: col.id, offset: cards.length, limit: 25 });
      setExtra(col.id, [...(extra || []), ...data.rows]);
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };
  return (
    <section
      className={`pl-col crm-col${cards.length ? "" : " is-empty"}${folded ? " is-folded" : ""}`} data-stage={STAGE[tone]} data-land="" data-col-id={col.id}
      data-testid={`pipeline-column-${col.id}`}
    >
      <header className="pl-col__head" data-testid="pipeline-column-header">
        <span className="pl-col__dot" />
        <span className="crm-col__names">{col.pipeline && <small className="pl-col-pipeline">{col.pipeline}</small>}<h3 className="pl-col__name" title={col.name}>{col.name}</h3></span>
        <span className="pl-col__count">{col.count.toLocaleString("fr-FR")}</span>
        {col.amount > 0 && <span className="pl-col__total">{euros(col.amount)}</span>}
        <button type="button" className="pl-col__fold" onClick={() => onFold(col.id)} title={folded ? "Déplier la colonne" : "Replier la colonne"} aria-label={folded ? "Déplier la colonne" : "Replier la colonne"} data-testid={`pipeline-column-fold-${col.id}`}>{folded ? <ChevronsLeftRight size={14} /> : <ChevronsRightLeft size={14} />}</button>
      </header>
      <div className="pl-col__body">
        {cards.map((lead) => <LeadCard key={lead.lead_id} lead={lead} tone={tone} dormantDays={dormantDays} onOpen={onOpen} />)}
        <div className="pl-col__empty"><span>Glissez une carte ici</span></div>
        {cards.length < col.count && (
          <button type="button" className="pl-more" onClick={more} disabled={loading} data-testid={`pipeline-column-more-${col.id}`}>
            {loading ? "Chargement…" : `Voir ${Math.min(25, col.count - cards.length)} de plus (${(col.count - cards.length).toLocaleString("fr-FR")} restantes)`}
          </button>
        )}
      </div>
    </section>
  );
};

// Pavé tactile : gauche-droite sur le tableau ; haut-bas laissé à la page
const sideScroll = (e) => {
  if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
  e.preventDefault();
  e.currentTarget.scrollLeft += e.deltaX;
};

// Flèches gauche/droite pour la souris classique (en plus du pavé tactile)
const SideArrows = ({ boardRef, frameRef }) => {
  const [st, setSt] = useState({ left: false, right: false, x0: 0, x1: 0 });
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    const update = () => {
      const r = frameRef.current?.parentElement?.getBoundingClientRect();
      const shown = el.getClientRects().length > 0;
      setSt({ left: shown && el.scrollLeft > 4, right: shown && el.scrollLeft + el.clientWidth < el.scrollWidth - 4, x0: r ? Math.max(r.left, 0) : 0, x1: r ? Math.min(r.right, window.innerWidth) : window.innerWidth });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [boardRef, frameRef]);
  const go = (dir) => boardRef.current?.scrollBy({ left: dir * boardRef.current.clientWidth * 0.7, behavior: "smooth" });
  return createPortal(<>
    {st.left && <button type="button" className="pl-arrow pl-arrow--left" style={{ left: st.x0 + 8 }} onClick={() => go(-1)} aria-label="Colonnes précédentes" data-testid="pipeline-scroll-left"><ChevronLeft size={22} /></button>}
    {st.right && <button type="button" className="pl-arrow pl-arrow--right" style={{ left: st.x1 - 56 }} onClick={() => go(1)} aria-label="Colonnes suivantes" data-testid="pipeline-scroll-right"><ChevronRight size={22} /></button>}
  </>, document.body);
};

export const PipelineBoard = ({ board, filters, onOpen, onMove, played }) => {
  const [extra, setExtraState] = useState({});
  const [folded, onFold] = useFolded();
  const setExtra = (id, rows) => setExtraState((prev) => ({ ...prev, [id]: rows }));
  // lâcher réussi : la carte est déplacée tout de suite et se pose depuis la position du fantôme
  const onDrop = (leadId, colId, from) => {
    const col = board.columns.find((c) => c.id === colId);
    const lead = board.columns.flatMap((c) => [...c.cards, ...(extra[c.id] || [])]).find((l) => l.lead_id === leadId);
    if (!col || !lead || lead.step_id === col.id) return false;
    onMove(lead, col);
    setExtraState((prev) => Object.fromEntries(Object.entries(prev).map(([k, rows]) => [k, rows.filter((r) => r.lead_id !== leadId)])));
    requestAnimationFrame(() => {
      const moved = document.querySelector(`[data-testid="pipeline-card-${leadId}"]`);
      if (!moved) return;
      moved.closest(".pl-col__body")?.scrollTo({ top: 0 });
      const r = moved.getBoundingClientRect();
      moved.classList.add("is-landed");
      gsap.from(moved, { x: from.left - r.left, y: from.top - r.top, rotation: 3, duration: dur(0.5), ease: "power3.out", clearProps: "transform" });
      setTimeout(() => moved.classList.remove("is-landed"), 1400);
    });
    return true;
  };
  const ref = useRef(null);
  useCardDrag(ref, onDrop);
  const boardRef = useRef(null);
  useEffect(() => {
    const el = boardRef.current;
    el?.addEventListener("wheel", sideScroll, { passive: false });
    return () => el?.removeEventListener("wheel", sideScroll);
  }, []);
  useEffect(() => {
    if (played.current) return undefined;
    played.current = true;
    return enterPage(ref.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
    <SideArrows boardRef={boardRef} frameRef={ref} />
    <div className="crm-board-view" data-view="pipeline" ref={ref}>
    <div className="pl-board crm-board" data-testid="pipeline-board" ref={boardRef}>
      {board.columns.map((col, i) => (
        <Column key={col.id} col={col} tone={TONES[i % TONES.length]} filters={filters} dormantDays={filters.dormant_days} onOpen={onOpen} onDrop={onDrop} extra={extra[col.id]} setExtra={setExtra} folded={folded.includes(col.id)} onFold={onFold} />
      ))}
    </div>
    </div>
    </>
  );
};
